/**
 * EventInstanceManager.ts
 *
 * 이벤트 인스턴스 생성 및 관리를 담당하는 모듈
 *
 * 주요 기능:
 * - 이벤트 인스턴스 생성 및 관리
 * - 반복 이벤트의 개별 인스턴스 처리
 * - 예외 날짜(exdate) 및 수정된 인스턴스(recurrence) 처리
 * - 시간대 변환 및 오프셋 처리
 * - 인스턴스 정렬 및 필터링
 * - UID 기반 인스턴스 검색
 *
 * 이 모듈은 마스터 이벤트를 실제 표시 가능한 인스턴스로 변환하여
 * 캘린더 뷰에서 올바르게 표시되도록 합니다.
 */

import moment from 'moment'
import 'moment-timezone'
import type { VEvent } from 'node-ical'
import settings from 'popups/Settings'
import * as dateUtils from '../utils/DateUtils'
import EventInstance from './EventInstance'
import { classInfo, currentDate, instanceEmitter } from './CalendarState'
import { searchEventByUid } from './EventManager'

/**
 * Calendar event data structure
 */
interface CalendarEvent {
  url: string
  etag: string
  event: VEvent
  color?: string
  folderId?: string
  dirty?: boolean
}

/**
 * Events dictionary keyed by URL
 */
export type EventsDict = Record<string, CalendarEvent>

/**
 * Remove instances from the global instance list
 */
export const _removeInstances = (events: EventsDict): void => {
  let instances = classInfo.instances
  let hasRemoved = false
  Object.entries(events).forEach(([key, _e]) => {
    instances = instances.filter((i) => {
      if (i.serverId === key) {
        hasRemoved = true
        return false
      }
      return true
    })
  })
  classInfo.instances = instances
  if (hasRemoved) {
    instanceEmitter.emit('instance')
  }
}

/**
 * Remove all instances belonging to a specific calendar
 */
export const removeInstanceByCalendar = (calendarUrl: string): void => {
  classInfo.instances = classInfo.instances.filter((i) => {
    return !i.serverId.startsWith(calendarUrl)
  })
  instanceEmitter.emit('instance')
}

/**
 * Create event instances from event data
 *
 * @param events - Dictionary of events to create instances from
 * @param hasRemoved - Whether instances were removed
 * @param orgInstances - Original instance array
 * @param createDangling - Whether to create dangling (not added to main list) instances
 * @param focusedDate - Date to focus on for recurring events
 * @returns Array of event instances
 */
export const _createInstances = (
  events: EventsDict,
  hasRemoved: boolean = false,
  orgInstances: EventInstance[] = [],
  createDangling: boolean = false,
  focusedDate: Date | null = null,
): EventInstance[] => {
  let instances = orgInstances
  let hasDirty = false
  let danglingInstances: EventInstance[] = []

  const calendarSettings = settings.getValue('calendar')
  const hideCanceled = calendarSettings.hideCanceledMeeting
  const hideDeclined = calendarSettings.hideDeclinedMeeting

  Object.entries(events).forEach(([_key, e]) => {
    if (!e.dirty) {
      return true
    }
    hasDirty = true
    e.dirty = false
    let masterEvent = e.event

    try {
      let rrule = masterEvent.rrule
      if (rrule === undefined) {
        // 단일 이벤트 인스턴스 생성
        const instance = new EventInstance(e.url, e.etag, e.color || '', masterEvent)
        if (!createDangling) {
          if ((hideCanceled && instance.isCanceled) || (hideDeclined && instance.isDeclined)) {
            return true
          }
          instances.push(instance)
        } else {
          danglingInstances.push(instance)
        }
      } else {
        const targetDate = focusedDate ? focusedDate : currentDate
        const eventTz = masterEvent.start?.tz || moment.tz.guess()
        // 반복 이벤트 인스턴스 생성
        const recurIds = rrule.between(
          dateUtils.getMonthViewFirstDate(dateUtils.getPrevMonth(targetDate)),
          dateUtils.getMonthViewLastDate(dateUtils.getNextMonth(targetDate)),
        )

        let exceptions = masterEvent.recurrences
        let exdates = masterEvent.exdate ?? {}
        recurIds.forEach((date) => {
          const dateStr = moment(date).tz(eventTz).format('YYYY-MM-DD')
          if (exdates && exdates[dateStr]) {
            //exdate에 있는 경우 deleted exception
            return
          }
          let exception: VEvent | null = null

          if (exceptions) {
            exception = (exceptions[dateStr] as VEvent | undefined) ?? null
          }
          const instance = new EventInstance(
            e.url,
            e.etag,
            e.color || '',
            masterEvent,
            date,
            exception,
          )
          if (!createDangling) {
            if ((hideCanceled && instance.isCanceled) || (hideDeclined && instance.isDeclined)) {
              return true
            }
            instances.push(instance)
          } else {
            danglingInstances.push(instance)
          }
        })
      }
    } catch (error) {
      console.error('Error creating instances:', error, masterEvent)
    }
    return true
  })

  // 인스턴스 정렬
  if (hasDirty || hasRemoved) {
    if (!createDangling) {
      instances.sort((a, b) => {
        let offset = a.startDate.getTime() - b.startDate.getTime()
        if (offset === 0) {
          offset = b.endDate.getTime() - a.endDate.getTime()
        }
        return offset
      })
    } else {
      danglingInstances.sort((a, b) => {
        let offset = a.startDate.getTime() - b.startDate.getTime()
        if (offset === 0) {
          offset = b.endDate.getTime() - a.endDate.getTime()
        }
        return offset
      })
    }
  }
  if (!createDangling) {
    return instances
  } else {
    return danglingInstances
  }
}

/**
 * Show previously hidden events (e.g., after changing settings)
 */
export const _showHiddenEvents = (): void => {
  classInfo.calendars?.forEach((c: any) => {
    const eventDict: EventsDict = {}
    const allOldEvents = { ...c.events }
    const instances = classInfo.instances

    Object.entries(allOldEvents).forEach(([key, e]: [string, any]) => {
      const index = instances.findIndex((i) => {
        if (i.serverId === key) {
          return true
        }
        return false
      })
      if (index === -1) {
        eventDict[e.url] = {
          url: e.url,
          etag: e.etag,
          color: c.calendarColor._cdata,
          event: e.event,
          folderId: c.url,
          dirty: true,
        }
      }
    })

    classInfo.instances = _createInstances(eventDict, false, instances)
    instanceEmitter.emit('instance')
  })
}

/**
 * Find an event instance by UID
 *
 * @param uid - Event UID
 * @param recurrenceId - Recurrence ID for specific instance (optional)
 * @param completeCallback - Callback with found instance or null
 */
export const findInstanceByUid = (
  uid: string,
  recurrenceId?: string | Date,
  completeCallback: (instance: EventInstance | null) => void = (_instance) => {},
): void => {
  const instance = classInfo.instances.find((i) => {
    if (recurrenceId) {
      return i.event?.uid === uid && i.exception?.recurrenceid === recurrenceId
    } else {
      return i.event?.uid === uid
    }
  })
  if (instance) {
    completeCallback(instance)
    return
  }

  const notProcessed = classInfo.calendars.every((c: any) => {
    const event = Object.entries(c.events).find(([_, e]: [string, any]) => e.event.uid === uid)
    if (event) {
      const eventData = event[1] as any
      const e: any = { ...eventData, dirty: true }
      const instances = _createInstances({ [e.url]: e }, false, [], true)
      if (recurrenceId) {
        const i = instances.find((i) => {
          return i.startDate === recurrenceId || i.exception?.recurrenceid === recurrenceId
        })
        completeCallback(i || null)
      } else {
        completeCallback(instances[0] || null)
      }
      return false
    }
    return true
  })
  if (!notProcessed) {
    return
  }

  searchEventByUid(uid, recurrenceId as any, (instance: EventInstance | null) => {
    if (instance) {
      completeCallback(instance)
    } else {
      completeCallback(null)
    }
  })
}
